import {
    Tab,
    Tabs,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Chip,
    Grid,
    Divider,
    Stack,
    Tooltip,
    Button,
    Container,
    Paper,
} from "@mui/material";
import { ChatBubbleOutlineOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Page from "../component/Page";
import NextBreadcrumbs from "../component/NextBreadcrumbs";
import KYCountyInterface from "../component/KYCountyInterface";

const toCsv = (rows, headers) => {
    const escape = (val) => {
        if (val == null) return "";
        const s = String(val);
        if (/[",\n]/.test(s)) {
            return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    };
    const headerLine = headers.map((h) => escape(h.label)).join(",");
    const body = rows.map((row) => headers.map((h) => escape(row[h.key])).join(",")).join("\n");
    return `${headerLine}\n${body}`;
};

const downloadCsv = (filename, rows, headers) => {
    if (!rows || rows.length === 0) return;
    const csv = toCsv(rows, headers);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export default function Stats() {
    const [isLoading, setIsLoading] = useState(true);
    const [countyTempRecords, setCountyTempRecords] = useState([]);
    const [statewideRecords, setStatewideRecords] = useState([]);
    const [selectedCountyId, setSelectedCountyId] = useState("path-Warren");
    const [sectionTab, setSectionTab] = useState("statewide");

    const handleExportStatewide = () => {
        const headers = [
            { key: "element", label: "Element" },
            { key: "value", label: "Value" },
            { key: "units", label: "Units" },
            { key: "date", label: "Date" },
            { key: "location", label: "Location" },
            { key: "stationId", label: "Station" },
            { key: "status", label: "Status" },
            { key: "notes", label: "Notes" },
        ];
        const rows = statewideRecords.map((r) => ({
            element: r.element || "",
            value: r.value ?? "",
            units: r.units || "",
            date: r.date || "",
            location: r.location || "",
            stationId: r.stationId || "",
            status: r.statusCode,
            notes: r.notes || "",
        }));
        downloadCsv("statewide-records.csv", rows, headers);
    };

    const handleExportCounty = () => {
        const headers = [
            { key: "county", label: "County" },
            { key: "maxTempValue", label: "Max Temperature Value" },
            { key: "maxTempUnits", label: "Max Temperature Units" },
            { key: "maxTempDate", label: "Max Temperature Date" },
            { key: "maxTempLocation", label: "Max Temperature Location" },
            { key: "maxTempNetwork", label: "Max Temperature Network Type" },
            { key: "minTempValue", label: "Min Temperature Value" },
            { key: "minTempUnits", label: "Min Temperature Units" },
            { key: "minTempDate", label: "Min Temperature Date" },
            { key: "minTempLocation", label: "Min Temperature Location" },
            { key: "minTempNetwork", label: "Min Temperature Network Type" },
            { key: "rainValue", label: "Total Rainfall Value" },
            { key: "rainUnits", label: "Total Rainfall Units" },
            { key: "rainDate", label: "Total Rainfall Date" },
            { key: "rainLocation", label: "Total Rainfall Location" },
            { key: "rainNetwork", label: "Total Rainfall Network Type" },
        ];
        const rows = countyTempRecords.map((r) => ({
            county: r.county || "",
            maxTempValue: r.max?.value ?? "",
            maxTempUnits: r.max?.units || "",
            maxTempDate: r.max?.date || "",
            maxTempLocation: r.max?.location || "",
            maxTempNetwork: r.max?.networkType || "",
            minTempValue: r.min?.value ?? "",
            minTempUnits: r.min?.units || "",
            minTempDate: r.min?.date || "",
            minTempLocation: r.min?.location || "",
            minTempNetwork: r.min?.networkType || "",
            rainValue: r.rain?.value ?? "",
            rainUnits: r.rain?.units || "",
            rainDate: r.rain?.date || "",
            rainLocation: r.rain?.location || "",
            rainNetwork: r.rain?.networkType || "",
        }));
        downloadCsv("county-records.csv", rows, headers);
    };

    const handleCountySelect = (id) => {
        setSelectedCountyId(id || null);
    };

    const normCounty = (s) => {
        if (!s) return "";
        return String(s)
            .replace(/^path[-_:\s]+/i, "")
            .replace(/^county[-_:\s]+/i, "")
            .replace(/county$/i, "")
            .replace(/parish$/i, "")
            .replace(/\bco\.?$/i, "")
            .replace(/[^a-z0-9]/gi, " ")
            .replace(/\s+/g, " ")
            .trim()
            .toUpperCase();
    };

    const displayNetworkType = (type) => {
        if (!type) return "";
        const raw = String(type).trim();
        if (!raw) return "";
        const key = raw.toLowerCase();
        const map = {
            coop: "COOP",
            cocorahs: "CoCoRaHS",
            mesonet: "Kentucky Mesonet",
            asos: "ASOS",
            awos: "AWOS",
            wb: "WBAN",
            wban: "WBAN",
        };
        return map[key] || raw;
    };

    const parseNetworkFromDescription = (description) => {
        const text = String(description || "");
        // Expected pattern: "... / <network> / <County> County."
        const match = text.match(/\/\s*([^\/]+?)\s*\/\s*[^\/]*County/i);
        return match ? match[1].trim() : "";
    };

    const getNetworkType = (info) => {
        const direct = displayNetworkType(info?.recordStationType);
        if (direct) return direct;

        const keyBased = displayNetworkType(info?.stationTypeKey);
        if (keyBased) return keyBased;

        return displayNetworkType(parseNetworkFromDescription(info?.recordDescription));
    };

    const countyIndex = useMemo(() => {
        const map = new Map();
        for (const c of countyTempRecords) {
            const kCounty = normCounty(c.county);
            const kId = normCounty(c.id);
            if (kCounty) map.set(kCounty, c);
            if (kId) map.set(kId, c);
            if (kCounty) {
                map.set(normCounty(`${c.county} County`), c);
                map.set(normCounty(`${c.county} Co.`), c);
            }
        }
        return map;
    }, [countyTempRecords]);

    const findCountyRecord = (idOrName) => {
        if (!idOrName) return null;
        const key = normCounty(idOrName);
        return countyIndex.get(key) || null;
    };

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                const statsRes = await fetch("https://kymn-json.s3.us-east-2.amazonaws.com/statsdata.json", {
                    cache: "no-store",
                });

                if (!statsRes.ok) {
                    setStatewideRecords([]);
                    setCountyTempRecords([]);
                    return;
                }

                const statsJson = await statsRes.json();
                const rowsRaw = Array.isArray(statsJson.data) ? statsJson.data : [];

                const stateRows = rowsRaw
                    .filter((r) => r.type === "state" && r.stateInfo)
                    .map((r, idx) => {
                        const s = r.stateInfo;
                        return {
                            id: r.id || `state-${idx}`,
                            element: s.recordTitle || "",
                            value: s.recordValue ?? null,
                            units: s.recordUnits || "",
                            date: s.recordDate || s.dateKey || null,
                            location: s.recordLocation || "",
                            stationId: s.recordStationType || "",
                            statusCode: s.recordStatus || "",
                            notes: s.recordNotes || "",
                        };
                    })
                    .sort((a, b) => {
                        const sa = rowsRaw.find((r) => r.id === a.id)?.stateInfo?.displayWeight ?? 0;
                        const sb = rowsRaw.find((r) => r.id === b.id)?.stateInfo?.displayWeight ?? 0;
                        return sb - sa;
                    });
                setStatewideRecords(stateRows);

                const countyRaw = rowsRaw.filter((r) => r.type === "county");
                const countyMap = new Map();
                for (const r of countyRaw) {
                    const s = r.countyInfo || r.stateInfo || {};
                    const countyName = s.recordCounty || s.recordLocation || s.countyKey || r.id || "Unknown";

                    const key = normCounty(countyName);
                    const title = s.recordTitle || s.titleKey || "";
                    const isMax = /max/i.test(title) || /maximum/i.test(title);
                    const isMin = /min/i.test(title) || /minimum/i.test(title);
                    const value = s.recordValue != null ? Number(s.recordValue) : null;
                    const category = String(s.recordCategoryKey || s.recordCategory || "").toLowerCase();

                    if (!key) continue;

                    const existing = countyMap.get(key) || {
                        id: r.id,
                        county: countyName,
                        max: null,
                        min: null,
                        rain: null,
                    };

                    const payload = {
                        value,
                        units: s.recordUnits || "",
                        date: s.recordDate || s.dateKey || null,
                        location: s.recordLocation || "",
                        networkType: getNetworkType(s),
                        notes: s.recordNotes || "",
                        statusCode: s.recordStatus || "",
                        title,
                        category,
                    };

                    const isTempCategory = /temp/.test(category) || /temperature/i.test(title);
                    if (isTempCategory) {
                        if (isMax) existing.max = payload;
                        if (isMin) existing.min = payload;
                    }

                    const isPrecipCategory = /precip|rain/.test(category) || /precipitation|rainfall/i.test(title);
                    if (isPrecipCategory && !existing.rain) {
                        existing.rain = payload;
                    }

                    countyMap.set(key, existing);
                }

                const countyRows = Array.from(countyMap.values()).sort((a, b) =>
                    String(a.county).localeCompare(String(b.county))
                );
                setCountyTempRecords(countyRows);
            } catch {
                setStatewideRecords([]);
                setCountyTempRecords([]);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    const categoryOf = (element = "") => {
        const s = String(element).toLowerCase();
        if (/temp|temperature|coldest|warmest|hottest|coolest|freeze|freezing|heat index|heatwave|heating degree|cooling degree/.test(s)) {
            return "Temperature";
        }
        if (/precip|precipitation|rain|rainfall|wettest|driest|storm total|drizzle/.test(s)) {
            return "Precipitation";
        }
        if (/snow|snowfall|snowiest|snowdepth|snow depth|blizzard/.test(s)) {
            return "Snow";
        }
        if (/wind|gust/.test(s)) {
            return "Wind";
        }
        return "Other";
    };

    const groupedStateRecords = () => {
        const groups = new Map();
        for (const r of statewideRecords) {
            const cat = categoryOf(r.element);
            if (!groups.has(cat)) groups.set(cat, []);
            groups.get(cat).push(r);
        }
        for (const [, arr] of groups) {
            arr.sort((a, b) => String(a.element).localeCompare(String(b.element)));
        }
        return groups;
    };

    return (
        <Page title="Statistics">
            <Container maxWidth="xl" sx={{ py: 5 }}>
                <NextBreadcrumbs title="Stats" parentLabel="KY Climate" parentHref="/timeline" />
                <Typography variant="h3">KY Climate</Typography>

                <Tabs value="stats" sx={{ mt: 2, mb: 3 }}>
                    <Tab label="Timeline" value="timeline" component={Link} href="/timeline" />
                    <Tab label="Statistics and Records" value="stats" component={Link} href="/stats" />
                </Tabs>

                <Tabs value={sectionTab} onChange={(_, value) => setSectionTab(value)} sx={{ mt: 2, mb: 3 }}>
                    <Tab label="Statewide Records" value="statewide" />
                    <Tab label="County Records" value="county" />
                </Tabs>

                {isLoading && sectionTab === "statewide" && (
                    <Box sx={{ mt: 2 }}>
                        <Skeleton variant="rectangular" height={260} />
                    </Box>
                )}

                {isLoading && sectionTab === "county" && (
                    <Box sx={{ mt: 2 }}>
                        <Skeleton variant="rectangular" height={520} />
                    </Box>
                )}

                {!isLoading && sectionTab === "statewide" && (
                    <>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="h4">Statewide Records</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FileDownloadOutlined />}
                                onClick={handleExportStatewide}
                            >
                                Export CSV
                            </Button>
                        </Stack>

                        {["Temperature", "Precipitation", "Snow", "Wind", "Other"].map((cat) => {
                            const groups = groupedStateRecords();
                            const list = groups.get(cat) || [];
                            if (!list.length) return null;
                            return (
                                <Box key={cat} sx={{ mt: 3 }}>
                                    <Typography variant="h5" sx={{ mb: 0.5 }}>
                                        {cat}
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ py: 0.5 }}>Element</TableCell>
                                                    <TableCell sx={{ py: 0.5 }} align="right">
                                                        Value
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5, width: 100 }}>Units</TableCell>
                                                    <TableCell sx={{ py: 0.5, width: 140 }}>Date</TableCell>
                                                    <TableCell sx={{ py: 0.5, width: 220 }}>Location</TableCell>
                                                    <TableCell sx={{ py: 0.5, width: 120 }}>Station</TableCell>
                                                    <TableCell sx={{ py: 0.5, width: 120 }}>Status</TableCell>
                                                    <TableCell sx={{ py: 0.5, maxWidth: 260 }}>Notes</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {list.map((row, idx) => (
                                                    <TableRow key={`${cat}-${idx}`} hover>
                                                        <TableCell sx={{ py: 0.25 }}>{row.element}</TableCell>
                                                        <TableCell sx={{ py: 0.25 }} align="right">
                                                            {row.value}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.25 }}>{row.units}</TableCell>
                                                        <TableCell sx={{ py: 0.25 }}>
                                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                                {row.date || "-"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.25, maxWidth: 220 }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                            >
                                                                {row.location || "-"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.25 }}>
                                                            {row.stationId ? (
                                                                <Tooltip title="Station ID">
                                                                    <Chip size="small" label={row.stationId} />
                                                                </Tooltip>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.25 }}>
                                                            {row.statusCode ? (
                                                                <Typography variant="caption" sx={{ color: "text.primary" }}>
                                                                    {row.statusCode}
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                                                                    No status
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.25, maxWidth: 260 }}>
                                                            {row.notes ? (
                                                                <Tooltip title={row.notes}>
                                                                    <Stack
                                                                        direction="row"
                                                                        spacing={0.5}
                                                                        alignItems="center"
                                                                        sx={{ maxWidth: 260 }}
                                                                    >
                                                                        <ChatBubbleOutlineOutlined sx={{ fontSize: 16, opacity: 0.7 }} />
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                color: "text.secondary",
                                                                                overflow: "hidden",
                                                                                textOverflow: "ellipsis",
                                                                                whiteSpace: "nowrap",
                                                                                flex: 1,
                                                                            }}
                                                                        >
                                                                            {row.notes}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Tooltip>
                                                            ) : (
                                                                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                                                                    No comments
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            );
                        })}
                    </>
                )}

                {!isLoading && sectionTab === "county" && (
                    <>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="h4">County Records</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FileDownloadOutlined />}
                                onClick={handleExportCounty}
                            >
                                Export CSV
                            </Button>
                        </Stack>
                        <Box sx={{ position: "relative", width: "100%", aspectRatio: "1 / 1", maxHeight: "50vh", mb: 0, pb: 0 }}>
                            <Box sx={{ position: "absolute", top: 0, right: 0, bottom: 0, left: { xs: 0, md: '460px' } }}>
                                <KYCountyInterface
                                    onSelect={handleCountySelect}
                                    onCountySelect={handleCountySelect}
                                    onClickCounty={handleCountySelect}
                                    onRegionSelect={handleCountySelect}
                                    selectedId={selectedCountyId}
                                    selectedFill="#ffe08a"
                                    fill="#fafafa"
                                    stroke="#999"
                                    strokeWidth={0.4}
                                    style={{ width: "100%", height: "100%", cursor: "pointer" }}
                                />
                            </Box>
                            {(() => {
                                const rec = findCountyRecord(selectedCountyId);
                                const hasSelection = Boolean(selectedCountyId);
                                if (!hasSelection) return null;

                                const title =
                                    rec?.county ||
                                    String(selectedCountyId || "")
                                        .split(/[-_:\s]+/)
                                        .slice(1)
                                        .join(" ") ||
                                    "Unknown";
                                return (
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            left: 8,
                                            p: 1.75,
                                            borderRadius: 1,
                                            width: 450,
                                            maxHeight: "90vh",
                                            zIndex: 2,
                                            backdropFilter: "blur(2px)",
                                        }}
                                    >
                                        <Stack spacing={0.75}>
                                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                                    {title} County Records
                                                </Typography>
                                                <Chip size="small" label="County" variant="outlined" />
                                            </Stack>
                                            <Divider />
                                            <Grid container spacing={1}>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                        Maximum Temperature
                                                    </Typography>
                                                    {rec?.max ? (
                                                        <>
                                                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                                                {rec.max.value}
                                                                <Typography
                                                                    component="span"
                                                                    variant="caption"
                                                                    sx={{ ml: 0.5, color: "text.secondary" }}
                                                                >
                                                                    {rec.max.units || "°F"}
                                                                </Typography>
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                                {rec.max.date || "-"}
                                                            </Typography>
                                                            <Tooltip title={rec.max.location || ""}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        display: "block",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {rec.max.location || "-"}
                                                                </Typography>
                                                            </Tooltip>
                                                            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                                                                Network: {rec.max.networkType || "-"}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                            No data
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                        Minimum Temperature
                                                    </Typography>
                                                    {rec?.min ? (
                                                        <>
                                                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                                                {rec.min.value}
                                                                <Typography
                                                                    component="span"
                                                                    variant="caption"
                                                                    sx={{ ml: 0.5, color: "text.secondary" }}
                                                                >
                                                                    {rec.min.units || "°F"}
                                                                </Typography>
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                                {rec.min.date || "-"}
                                                            </Typography>
                                                            <Tooltip title={rec.min.location || ""}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        display: "block",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {rec.min.location || "-"}
                                                                </Typography>
                                                            </Tooltip>
                                                            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                                                                Network: {rec.min.networkType || "-"}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                            No data
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                {rec?.rain && (
                                                    <Grid size={{ xs: 12 }} sx={{ mt: 0.5 }}>
                                                        <Divider sx={{ mb: 0.5 }} />
                                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                            Total Rainfall
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                                                            {rec.rain.value}
                                                            <Typography
                                                                component="span"
                                                                variant="caption"
                                                                sx={{ ml: 0.5, color: "text.secondary" }}
                                                            >
                                                                {rec.rain.units || "in"}
                                                            </Typography>
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                            {rec.rain.date || "-"}
                                                        </Typography>
                                                        <Tooltip title={rec.rain.location || ""}>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display: "block",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {rec.rain.location || "-"}
                                                            </Typography>
                                                        </Tooltip>
                                                        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                                                            Network: {rec.rain.networkType || "-"}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Stack>
                                    </Paper>
                                );
                            })()}
                        </Box>
                    </>
                )}
            </Container>
        </Page>
    );
}
